import React, { useRef } from "react"
import { Fragment, useState, useEffect } from 'react'
import { Popover, Transition, PopoverButton, PopoverPanel, PopoverGroup } from '@headlessui/react'
import Globals from "../hooks/globals"
import Cookies from "universal-cookie"
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ExLink from "./ex_link"
import { faBook, faNewspaper, faBolt, faSeedling, faCode, faLayerGroup, faSquareDollar } from "@fortawesome/pro-regular-svg-icons"
import { faArrowRight, faSignalBars } from "@fortawesome/pro-solid-svg-icons"
import railsIcon from "../images/icongrid/ruby_on_rails.svg"
import reactIcon from "../images/icongrid/react.svg"
import staticIcon from "../images/icons/cdn.svg"
import frameworkDeploymentIcon from "../images/icons/framework_deployment.svg"
import expressIconDark from "../images/icongrid/expressjs_dark.svg"
import PHP from "../images/icongrid/php.svg"
const mainLinks = [
    {
        name: 'Features',
        href: '/features',
        icon: faLayerGroup,
    },
    {
        name: 'Pricing',
        href: '/pricing',
        icon: faSquareDollar
    },
]
const resources = [
    {
        name: 'Documentation',
        description: 'Learn about Cloud 66',
        href: 'https://help.cloud66.com',
        fontAwesome: true,
        icon: faBook,
    },
    {
        name: 'Blog',
        description: 'Read our latest posts',
        href: 'https://blog.cloud66.com',
        fontAwesome: true,
        icon: faNewspaper,
    },
    {
        name: 'Changelog',
        description: 'See what\'s new',
        href: 'https://changelog.cloud66.com',
        fontAwesome: true,
        icon: faBolt,
    },
    {
        name: 'RealScale',
        description: 'Scaling Cloud Web Applications',
        href: 'https://realscale.cloud66.com',
        fontAwesome: true,
        icon: faSeedling,
    },
    {
        name: 'Status',
        description: 'Check the status of our services',
        href: 'https://status.cloud66.com',
        fontAwesome: true,
        icon: faSignalBars,
    },
]

const cloud66For = [
    {
        name: 'Rails',
        description: 'Everything you need to deploy Rails on any server',
        href: '/for/rails',
        fontAwesome: false,
        icon: railsIcon,
    },
    {
        name: 'React',
        description: 'SPAs, Server Side Components and other React applications',
        href: '/for/react',
        fontAwesome: false,
        icon: reactIcon,
    },
    {
        name: 'Express',
        description: 'Express and other Node.js frameworks',
        href: '/for/express',
        fontAwesome: false,
        icon: expressIconDark,
    },
    {
        name: 'Laravel',
        description: 'PHP frameworks like Laravel and Symfony',
        href: '/for/php',
        fontAwesome: false,
        icon: PHP,
    },
    {
        name: 'Python, Go, Rust, and more',
        description: 'Deploy any application on any server',
        href: '/for/other',
        fontAwesome: false,
        icon: frameworkDeploymentIcon,
    },
    {
        name: 'Static Sites',
        description: 'Gatsby, Jekyll, Hugo, and other static site generators',
        href: '/for/static',
        fontAwesome: false,
        icon: staticIcon,
    }
]

const LoginButtons = () => {
    const cookies = new Cookies()
    const [userName, setUsername] = useState(undefined);
    const [showNothing, setShowNothing] = useState(true)
    const [existingUser, setExistingUser] = useState(false)

    useEffect(() => {
        setUsername(cookies.get("user_name"))
    }, [])

    useEffect(() => {
        setShowNothing(false)
        setExistingUser(userName !== undefined)
    }, [userName])

    if (showNothing) {
        return (<div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0"></div>)
    }

    return (
        <div className="hidden md:flex items-center justify-end md:flex-1 lg:w-0 space-x-8">
            <span className="inline-flex rounded-md shadow-sm">
                <ExLink
                    to={
                        existingUser
                            ? "https://app.cloud66.com/dashboard"
                            : "https://app.cloud66.com/users/sign_in"
                    }
                    className="whitespace-nowrap items-center px-4 py-2 text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-gray-200 hover:bg-slate-500 text-gray-700 hover:text-gray-100 border border-transparent"
                >
                    {existingUser ?
                        <span className="flex items-center">
                            Dashboard
                            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                        </span> : "Sign in"}
                </ExLink>
            </span>
            {!existingUser && (
                <span className="inline-flex whitespace-nowrap rounded-md shadow-sm">
                    <ExLink
                        to="https://app.cloud66.com/users/sign_up?p=ec73a7"
                        className="items-center px-4 py-2 text-center text-base font-medium rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 bg-blue-400 hover:bg-blue-600 text-white border border-transparent"
                    >
                        Get Started
                    </ExLink>
                </span>
            )}
        </div>
    )
}

const FlipMenu = ({ title, items, columns = 2 }) => {
    let columnClass = `grid-cols-2`
    switch (columns) {
        case 1:
            columnClass = `grid-cols-1`
            break
        case 2:
            columnClass = `grid-cols-2`
            break
        case 3:
            columnClass = `grid-cols-3`
            break
    }

    return (
        <Popover className="relative">
            {({ open }) => (
                <>
                    <PopoverButton
                        className={Globals.classNames(
                            open ? 'text-gray-100' : 'text-gray-300',
                            'group rounded-md text-base inline-flex items-center font-medium hover:text-gray-100 focus:outline-none'
                        )}
                    >
                        <span>{title}</span>
                        <ChevronDownIcon
                            className={Globals.classNames(
                                open ? 'text-gray-100' : 'text-gray-300',
                                'ml-2 h-5 w-5 group-hover:text-gray-100'
                            )}
                            aria-hidden="true"
                        />
                    </PopoverButton>

                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="opacity-0 translate-y-1"
                        enterTo="opacity-100 translate-y-0"
                        leave="transition ease-in duration-150"
                        leaveFrom="opacity-100 translate-y-0"
                        leaveTo="opacity-0 translate-y-1"
                    >
                        <PopoverPanel className="bg-gray-100 rounded-xl absolute z-50 -ml-4 mt-3 transform px-2 sm:px-0 lg:ml-0 lg:left-1/2 lg:-translate-x-1/2">
                            <div className="rounded-lg shadow-lg ring-1 ring-white ring-opacity-5 overflow-hidden w-screen max-w-lg">
                                <div className={`relative grid ${columnClass} gap-6 px-5 py-6 sm:gap-8 sm:p-5`}>
                                    {items.map((item) => (
                                        <ExLink
                                            key={item.name}
                                            to={item.href}
                                            className="-m-3 p-3 flex items-center rounded-xl hover:bg-gray-200 h-20"
                                        >
                                            {(item.fontAwesome) ? (
                                                <FontAwesomeIcon icon={item.icon} className="flex-shrink-0 h-5 w-5 text-blue-600" aria-hidden="true" />) : (
                                                <img src={item.icon} className="flex-shrink-0 h-6 w-6 text-gray-400" aria-hidden="true" />)
                                            }
                                            <div className="ml-4">
                                                <p className="text-base font-medium text-gray-900">{item.name}</p>
                                                <p className="mt-1 text-sm text-gray-700">{item.description}</p>
                                            </div>
                                        </ExLink>
                                    ))}
                                </div>
                            </div>
                        </PopoverPanel>
                    </Transition>
                </>
            )}
        </Popover>
    )
}


export const Header = React.forwardRef(({ className, autoHide }, ref) => {
    const headerRef = useRef(null);
    const header = headerRef.current;

    useEffect(() => {
        if (!autoHide) return;

        const handleScroll = (event) => {
            if (!header) return;

            const scroll = window.scrollY
            if (scroll >= 50) {
                header.classList.replace('py-6', 'py-2')
            } else {
                header.classList.replace('py-2', 'py-6')
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [autoHide, header]);

    return (
        <header className={Globals.classNames(className, autoHide ? 'sticky top-0 z-50 isolation-auto' : '', 'bg-black/80 drop-shadow-2xl transition-all duration-300')} ref={ref}>
            <Popover className="relative">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div ref={headerRef} className="flex justify-between items-center py-6 md:justify-start md:space-x-10 transition-all duration-300">
                        <div className="justify-start flex">
                            <ExLink to="/">
                                <span className="sr-only">Cloud 66</span>
                                <img
                                    className="h-8 w-auto sm:h-10"
                                    src={Globals.currentLogo()}
                                    alt="Cloud 66"
                                />
                            </ExLink>
                        </div>
                        <div className="-mr-2 -my-2 md:hidden">
                            <PopoverButton className="bg-white/10 rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-800 focus:outline-none">
                                <span className="sr-only">Open menu</span>
                                <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                            </PopoverButton>
                        </div>
                        <PopoverGroup as="nav" className="hidden transform md:flex space-x-4 lg:space-x-10">
                            <ExLink to="/features" className="text-base whitespace-nowrap font-medium text-gray-300 hover:text-gray-100">
                                Features
                            </ExLink>
                            <FlipMenu title="Frameworks" items={cloud66For} columns={1} />
                            <FlipMenu title="Resources" items={resources} />
                            <ExLink to="/pricing" className="text-base font-medium text-gray-300 hover:text-gray-100">Pricing</ExLink>
                        </PopoverGroup>
                        {LoginButtons()}
                    </div>
                </div>

                <Transition
                    as={Fragment}
                    enter="duration-200 ease-out"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="duration-100 ease-in"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <PopoverPanel focus className="absolute top-0 inset-x-0 p-2 transition transform origin-top-right md:hidden">
                        <div className="rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 bg-gray-200 divide-y-2 divide-gray-50 max-h-[90vh] overflow-y-auto">
                            <div className="pt-5 pb-6 px-5">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <img
                                            className="h-8 w-auto"
                                            src={Globals.currentLogo()}
                                            alt="Cloud 66"
                                        />
                                    </div>
                                    <div className="-mr-2">
                                        <PopoverButton className="bg-gray-200 rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                                            <span className="sr-only">Close menu</span>
                                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                        </PopoverButton>
                                    </div>
                                </div>
                                <div className="mt-6">
                                    <nav className="grid gap-y-8">
                                        {mainLinks.map((item) => (
                                            <ExLink
                                                key={item.name}
                                                to={item.href}
                                                className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                                            >
                                                <FontAwesomeIcon icon={item.icon} className="flex-shrink-0 h-6 w-6 text-blue-600" aria-hidden="true" />
                                                <span className="ml-3 text-base font-medium text-gray-900">{item.name}</span>
                                            </ExLink>
                                        ))}
                                    </nav>
                                </div>
                                <div className="mt-8">
                                    <nav className="grid gap-y-8">
                                        {cloud66For.map((item) => (
                                            <ExLink
                                                key={item.name}
                                                to={item.href}
                                                className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                                            >
                                                {(item.fontAwesome) ? (
                                                    <FontAwesomeIcon icon={item.icon} className="flex-shrink-0 h-6 w-6 text-blue-600" aria-hidden="true" />) : (
                                                    <img src={item.icon} className="flex-shrink-0 h-6 w-6 text-gray-400" aria-hidden="true" />)
                                                }
                                                <span className="ml-3 text-base font-medium text-gray-900">Cloud 66 for {item.name}</span>
                                            </ExLink>
                                        ))}
                                    </nav>
                                </div>
                                <div className="mt-8">
                                    <nav className="grid gap-y-8">
                                        {resources.map((item) => (
                                            <ExLink
                                                key={item.name}
                                                to={item.href}
                                                className="-m-3 p-3 flex items-center rounded-md hover:bg-gray-50"
                                            >
                                                <FontAwesomeIcon icon={item.icon} className="flex-shrink-0 h-6 w-6 text-blue-600" aria-hidden="true" />
                                                <span className="ml-3 text-base font-medium text-gray-900">{item.name}</span>
                                            </ExLink>
                                        ))}
                                    </nav>
                                </div>
                            </div>
                            <div className="py-6 px-5 space-y-6">
                                <div>
                                    <ExLink
                                        to="https://app.cloud66.com/users/sign_up?p=ec73a7"
                                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
                                    >
                                        Sign up
                                    </ExLink>
                                    <p className="mt-6 text-center text-base font-medium text-gray-500">
                                        Existing customer?{' '}
                                        <ExLink to="https://app.cloud66.com/users/sign_in" className="text-blue-600 hover:text-blue-500">
                                            Sign in
                                        </ExLink>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </PopoverPanel>
                </Transition>
            </Popover>
        </header>
    )
})